/**
 * Advanced tracer for telemetry
 * Project ID: proj_cf1503afdeb74a70abec16b235bce7f5
 */

import simpleTracer from './simpleTracer.js';

// Import OpenTelemetry API - will be mocked in tests
import * as api from '@opentelemetry/api';

const trace = api.trace || {
  getTracer: () => simpleTracer,
  getActiveSpan: () => null,
  setSpan: () => {},
  deleteSpan: () => {}
};

const context = api.context || {
  active: () => ({}),
  with: (ctx, fn) => fn()
};

const SpanStatusCode = api.SpanStatusCode || { OK: 1, ERROR: 2 };
const SpanKind = api.SpanKind || { INTERNAL: 0, CLIENT: 3, SERVER: 4 };

// Store tracers by name
const tracers = new Map();
let activeSpan = null;

class Tracer {
  constructor(serviceName = 'app', version = '1.0.0') {
    this.serviceName = serviceName;
    this.version = version;
    this.provider = null;
    this.otelTracer = null;
    this.tracer = simpleTracer;
    this.spans = new Map();
    this.metrics = new Map();
  }

  initialize(config = {}) {
    // Initialize OpenTelemetry or other tracing provider
    this.config = config;
    // Get OpenTelemetry tracer
    this.otelTracer = trace.getTracer(this.serviceName, this.version);
    // Only log in non-test environments
    if (!import.meta.env.TEST) {
      console.log(`Tracer initialized for ${this.serviceName} v${this.version}`);
    }
  }

  startSpan(name, options = {}) {
    if (this.otelTracer && this.otelTracer.startSpan) {
      return this.otelTracer.startSpan(name, options);
    }
    return this.tracer.startSpan(name, options);
  }

  startActiveSpan(name, fn, options = {}) {
    if (this.otelTracer && this.otelTracer.startActiveSpan) {
      return this.otelTracer.startActiveSpan(name, fn, options);
    }
    const span = this.startSpan(name, options);
    try {
      const result = fn(span);
      span.end();
      return result;
    } catch (error) {
      span.recordException(error);
      span.end('error');
      throw error;
    }
  }

  async startActiveSpanAsync(name, fn, options = {}) {
    const span = this.startSpan(name, options);
    try {
      const result = await fn(span);
      span.end();
      return result;
    } catch (error) {
      span.recordException(error);
      span.end('error');
      throw error;
    }
  }

  recordException(error, span) {
    if (span) {
      span.recordException(error);
    } else {
      this.tracer.recordError(error);
    }
  }

  getTracer() {
    return this.otelTracer || this.tracer;
  }

  shutdown() {
    // Cleanup
    this.spans.clear();
    this.metrics.clear();
  }
}

// Create singleton
let tracerInstance = null;

export function createTracer(serviceName = 'default-tracer', version = '1.0.0') {
  const key = `${serviceName}:${version}`;
  
  if (!tracers.has(key)) {
    try {
      const otelTracer = trace.getTracer(serviceName, version);
      
      // Create a wrapper object that delegates to the OpenTelemetry tracer
      const tracerWrapper = {
        startSpan: (name, attributes = {}, options) => {
          // Handle both 2 and 3 argument forms
          if (options === undefined && typeof attributes === 'object') {
            return otelTracer.startSpan(name, attributes);
          }
          return otelTracer.startSpan(name, attributes, options);
        },
        startActiveSpan: (name, fn, options) => {
          if (typeof fn === 'function' && !options) {
            return otelTracer.startActiveSpan(name, fn);
          }
          return otelTracer.startActiveSpan(name, fn, options);
        }
      };
      
      tracers.set(key, tracerWrapper);
      
      // Also create our wrapper if needed
      if (!tracerInstance) {
        tracerInstance = new Tracer(serviceName, version);
        tracerInstance.initialize();
      }
    } catch (error) {
      console.error('Failed to create tracer:', error);
      return null;
    }
  }
  
  return tracers.get(key);
}

export function getActiveSpan() {
  // Try OpenTelemetry first
  if (trace && trace.getActiveSpan) {
    return trace.getActiveSpan();
  }
  return activeSpan;
}

export function setActiveSpan(span) {
  activeSpan = span;
  if (trace && trace.setSpan) {
    const ctx = context.active();
    return trace.setSpan(ctx, span);
  }
}

export function withSpan(span, fn) {
  const previousSpan = activeSpan;
  activeSpan = span;
  
  try {
    if (context && context.with) {
      const ctx = trace.setSpan(context.active(), span);
      return context.with(ctx, fn);
    }
    return fn();
  } finally {
    activeSpan = previousSpan;
  }
}

export function recordException(error, span) {
  const targetSpan = span || getActiveSpan();
  if (targetSpan && targetSpan.recordException) {
    targetSpan.recordException(error);
  }
}

export function addAttribute(key, value, span) {
  const targetSpan = span || getActiveSpan();
  if (targetSpan && targetSpan.setAttribute) {
    targetSpan.setAttribute(key, value);
  }
}

export function recordEvent(name, attributes, span) {
  const targetSpan = span || getActiveSpan();
  if (targetSpan && targetSpan.addEvent) {
    targetSpan.addEvent(name, attributes);
  }
}

export function setStatus(code, message, span) {
  const targetSpan = span || getActiveSpan();
  if (targetSpan && targetSpan.setStatus) {
    targetSpan.setStatus({ code, message });
  }
}

export { Tracer, SpanStatusCode, SpanKind };
// Don't create default instance - let consumers create their own
export const tracer = null;
export default createTracer;